import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { formatDistance } from 'date-fns';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import { LatLngTuple } from 'leaflet';
import { sessionsService } from '../../services';
import { useWebSocket, Position } from '../../hooks/useWebSocket';
import { SessionListSidebar } from './SessionListSidebar';
import { Session } from '../../types/session';
import { SessionMarker } from './SessionMarker';

// Component to handle map interactions
const MapUpdater: React.FC<{ selectedSession: Session | null; positions: Map<string, Position> }> = ({ selectedSession, positions }) => {
  const map = useMap();

  useEffect(() => {
    if (selectedSession) {
      const position = positions.get(selectedSession.sessionId);
      if (position) {
        // Zoom to the selected session position
        map.setView([position.latitude, position.longitude], 16);
      }
    }
  }, [selectedSession, positions, map]);

  return null;
};

const defaultCenter: [number, number] = [50.8503, 4.3517]; // Brussels
const defaultZoom = 12;

export const LiveMapPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialSessionId = searchParams.get('sessionId');

  const [sessions, setSessions] = useState<Session[]>([]);
  const [positions, setPositions] = useState<Map<string, Position>>(
    new Map()
  );
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(true);
  const [showHistorical, setShowHistorical] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [endingSessionId, setEndingSessionId] = useState<string | null>(null);
  const [lastUpdateSource, setLastUpdateSource] = useState<'websocket' | 'polling'>('websocket');
  const sessionIdsRef = useRef<string[]>([]);

  const wsEndpoint = (process.env.REACT_APP_WS_ENDPOINT ||
    'wss://0utl0r0lvj.execute-api.eu-west-3.amazonaws.com/dev').replace(
    'https://',
    'wss://'
  );

  // Fetch active sessions
  const {
    data: sessionsData,
    isLoading: isLoadingSessions,
    refetch: refetchSessions,
  } = useQuery({
    queryKey: ['sessions', 'active'],
    queryFn: () => sessionsService.getActiveSessions(),
    refetchInterval: isAutoRefreshEnabled ? 5000 : false, // 5 seconds if auto-refresh enabled (faster updates)
  });

  // WebSocket callbacks (memoized to prevent reconnection loops)
  const handlePosition = useCallback((position: Position) => {
    setLastUpdateSource('websocket'); // Mark source as WebSocket
    setPositions((prev) => {
      const newMap = new Map(prev);
      newMap.set(position.sessionId, position);
      return newMap;
    });
    setLastUpdated(new Date());
  }, []);

  const handleWSError = useCallback((error: Error) => {
    console.error('[LiveMapPage] WebSocket error:', error);
  }, []);

  const handleConnectionChange = useCallback((connected: boolean) => {
    console.log('[LiveMapPage] WebSocket connection:', connected);
  }, []);

  // WebSocket for real-time positions
  const { isConnected } = useWebSocket({
    wsEndpoint,
    sessionIds: sessionIdsRef.current,
    onPosition: handlePosition,
    onError: handleWSError,
    onConnectionChange: handleConnectionChange,
  });

  // Load historical positions for sessions
  const loadHistoricalPositions = useCallback(async (sessionIds: string[]) => {
    try {
      for (const sessionId of sessionIds) {
        const positions = await sessionsService.getSessionPositions(sessionId);
        if (positions.length > 0) {
          // Use the most recent position
          const latestPosition = positions[positions.length - 1];
          setPositions((prev) => {
            const newMap = new Map(prev);
            newMap.set(sessionId, {
              sessionId,
              timestamp: latestPosition.timestamp,
              latitude: latestPosition.latitude,
              longitude: latestPosition.longitude,
              accuracy: latestPosition.accuracy,
              speed: latestPosition.speed,
              heading: latestPosition.heading
            });
            return newMap;
          });
        }
      }
    } catch (error) {
      console.error('[LiveMapPage] Error loading historical positions:', error);
    }
  }, []);

  // Update sessions and sync WebSocket subscriptions
  useEffect(() => {
    if (sessionsData) {
      setSessions(sessionsData);

      // Update WebSocket subscriptions
      const newSessionIds = sessionsData.map((s) => s.sessionId);
      sessionIdsRef.current = newSessionIds;

      // Load historical positions for all sessions
      loadHistoricalPositions(newSessionIds);

      // Auto-select session from URL parameter if provided
      if (initialSessionId) {
        const sessionToSelect = sessionsData.find((s) => s.sessionId === initialSessionId);
        if (sessionToSelect) {
          setSelectedSession(sessionToSelect);
        }
      }
    }
  }, [sessionsData, loadHistoricalPositions, initialSessionId]);

  // Refresh positions from API (polling fallback)
  const refreshPositionsFromApi = useCallback(async () => {
    if (sessions.length === 0) return;

    try {
      setLastUpdateSource('polling'); // Mark source as polling
      for (const session of sessions) {
        const positions = await sessionsService.getSessionPositions(session.sessionId);
        if (positions.length > 0) {
          // Use the most recent position
          const latestPosition = positions[positions.length - 1];
          setPositions((prev) => {
            const newMap = new Map(prev);
            newMap.set(session.sessionId, {
              sessionId: session.sessionId,
              timestamp: latestPosition.timestamp,
              latitude: latestPosition.latitude,
              longitude: latestPosition.longitude,
              accuracy: latestPosition.accuracy,
              speed: latestPosition.speed,
              heading: latestPosition.heading
            });
            return newMap;
          });
        }
      }
    } catch (error) {
      console.error('[LiveMapPage] Error refreshing positions from API:', error);
    }
  }, [sessions]);

  // Poll positions from API (fallback only if WebSocket disconnected)
  useEffect(() => {
    // Skip polling if WebSocket is connected (use WebSocket for real-time updates)
    if (!isAutoRefreshEnabled || sessions.length === 0 || isConnected) return;

    // Fallback polling with increased interval (30s instead of 5s)
    console.log('[LiveMap] Polling fallback (WebSocket disconnected)');
    const interval = setInterval(() => {
      refreshPositionsFromApi();
    }, 30000); // 30 seconds for fallback polling

    return () => clearInterval(interval);
  }, [isAutoRefreshEnabled, sessions, refreshPositionsFromApi, isConnected]);

  // Handle session selection
  const handleSessionSelect = useCallback((session: Session) => {
    setSelectedSession(session);
  }, []);

  // Manual refresh
  const handleRefresh = useCallback(() => {
    refetchSessions();
    refreshPositionsFromApi();
  }, [refetchSessions, refreshPositionsFromApi]);

  // Toggle auto-refresh
  const handleToggleAutoRefresh = useCallback(() => {
    setIsAutoRefreshEnabled((prev) => !prev);
  }, []);

  // Get time since last update
  const getTimeSinceUpdate = (): string => {
    if (!lastUpdated) return 'Never';
    return formatDistance(lastUpdated, new Date(), { addSuffix: true });
  };

  // Count sessions with positions
  const sessionsWithPositions = sessions.filter((s) =>
    positions.has(s.sessionId)
  );

  // Handle end session
  const handleEndSession = useCallback(async (sessionId: string) => {
    if (!window.confirm('Are you sure you want to end this session?')) {
      return;
    }

    try {
      setEndingSessionId(sessionId);
      await sessionsService.endSession(sessionId);
      await refetchSessions();
    } catch (error) {
      console.error('Failed to end session:', error);
      alert('Failed to end session. Please try again.');
    } finally {
      setEndingSessionId(null);
    }
  }, [refetchSessions]);

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <SessionListSidebar
        sessions={sessions}
        selectedSessionId={selectedSession?.sessionId}
        onSessionSelect={handleSessionSelect}
        onRefresh={handleRefresh}
        onEndSession={handleEndSession}
        endingSessionId={endingSessionId}
        isLoading={isLoadingSessions}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Top Controls */}
        <div className="bg-white shadow-sm border-b border-gray-200 p-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-gray-900">Live Tracking</h1>
            <div className="flex items-center gap-2">
              <div
                className={`w-3 h-3 rounded-full ${
                  isConnected ? 'bg-green-500' : 'bg-red-500'
                }`}
              />
              <span className="text-sm text-gray-600">
                {isConnected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
            <div className="text-sm text-gray-500">
              Updated: {getTimeSinceUpdate()} • {sessionsWithPositions.length}/{sessions.length} sessions tracked
            </div>
            <div className="text-xs text-gray-500">
              Source: {lastUpdateSource === 'websocket' ? '🟢 WebSocket' : '🔴 Polling (fallback)'}
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setShowHistorical(!showHistorical)}
              className={`px-4 py-2 rounded-lg transition font-medium ${
                showHistorical
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-900'
              }`}
            >
              {showHistorical ? 'Show Latest' : 'Show History'}
            </button>

            <button
              onClick={handleToggleAutoRefresh}
              className={`px-4 py-2 rounded-lg transition font-medium ${
                isAutoRefreshEnabled
                  ? 'bg-green-600 text-white'
                  : 'bg-yellow-600 text-white'
              }`}
            >
              {isAutoRefreshEnabled ? 'Pause' : 'Resume'}
            </button>
          </div>
        </div>

        {/* Map Container */}
        <div className="flex-1 relative bg-gray-100">
          {sessions.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <p className="text-xl text-gray-600 mb-4">
                  No active sessions
                </p>
                <button
                  onClick={handleRefresh}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Refresh
                </button>
              </div>
            </div>
          ) : (
            <MapContainer
              center={defaultCenter as LatLngTuple}
              zoom={defaultZoom}
              className="h-full w-full"
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              {/* Map updater for auto-zoom on selection */}
              <MapUpdater selectedSession={selectedSession} positions={positions} />

              {/* Markers for all sessions with positions */}
              {sessions.map((session) => {
                const position = positions.get(session.sessionId);
                if (!position) return null;

                return (
                  <SessionMarker
                    key={session.sessionId}
                    session={session}
                    position={position}
                    isSelected={selectedSession?.sessionId === session.sessionId}
                    onClick={() => handleSessionSelect(session)}
                  />
                );
              })}
            </MapContainer>
          )}
        </div>
      </div>
    </div>
  );
};
